/**
 * Funnel Tracker Service
 * Tracks user interactions across the sales funnel with metrics at every stage.
 */

const API_BASE = process.env.REACT_APP_AUTH_API
  || (process.env.NODE_ENV === 'production' ? '/api/funnel' : 'http://localhost:8080/api/funnel');

// ============================================
// SESSION & UTM MANAGEMENT
// ============================================

/** Generate a unique session ID per visit */
export function generateSessionId(): string {
  const existing = sessionStorage.getItem('funnel_session_id');
  if (existing) return existing;

  const id = `fs_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  sessionStorage.setItem('funnel_session_id', id);
  return id;
}

/** Extract UTM params from current URL */
export function getUtmParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];
  const utms: Record<string, string> = {};

  for (const key of utmKeys) {
    const value = params.get(key);
    if (value) utms[key] = value;
  }

  // Persist UTMs for the session so they survive navigation
  if (Object.keys(utms).length > 0) {
    sessionStorage.setItem('funnel_utms', JSON.stringify(utms));
  }

  const stored = sessionStorage.getItem('funnel_utms');
  return stored ? JSON.parse(stored) : utms;
}

// ============================================
// EVENT TRACKING
// ============================================

export interface FunnelEventData {
  event_name: string;
  event_data?: Record<string, unknown>;
  lead_id?: number;
}

/** Send a tracking event to the backend */
export async function trackEvent(
  eventName: string,
  eventData: Record<string, unknown> = {}
): Promise<void> {
  try {
    const sessionId = generateSessionId();
    const utms = getUtmParams();

    const payload = {
      session_id: sessionId,
      event_name: eventName,
      event_data: eventData,
      page_url: window.location.pathname + window.location.search,
      referrer_url: document.referrer || null,
      ...utms,
    };

    // Use sendBeacon for reliability (doesn't block navigation)
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    const sent = navigator.sendBeacon(`${API_BASE}/event.php`, blob);

    // Fallback to fetch if sendBeacon fails
    if (!sent) {
      await fetch(`${API_BASE}/event.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      });
    }
  } catch (error) {
    // Tracking should never break user experience
    console.warn('[FunnelTracker] Failed to track event:', eventName, error);
  }
}

// ============================================
// SECTION VIEW TRACKING (IntersectionObserver)
// ============================================

const viewedSections = new Set<string>();

/** Track when a section enters the viewport (fires once per section per session) */
export function trackSectionView(
  sectionName: string,
  element: HTMLElement | null
): IntersectionObserver | null {
  if (!element) return null;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !viewedSections.has(sectionName)) {
          viewedSections.add(sectionName);
          trackEvent(`${sectionName}_viewed`, {
            timestamp: Date.now(),
          });
        }
      }
    },
    { threshold: 0.3 } // 30% visible triggers the event
  );

  observer.observe(element);
  return observer;
}

// ============================================
// VIDEO PROGRESS TRACKING
// ============================================

const trackedMilestones = new Set<number>();

/** Track video playback milestones (25%, 50%, 75%, 100%) */
export function trackVideoProgress(percentWatched: number): void {
  const milestones = [25, 50, 75, 100];

  for (const milestone of milestones) {
    if (percentWatched >= milestone && !trackedMilestones.has(milestone)) {
      trackedMilestones.add(milestone);
      trackEvent(`vsl_${milestone}pct`, { percent: milestone });
    }
  }
}

/** Track video play start */
export function trackVideoPlay(): void {
  trackEvent('vsl_play');
}

// ============================================
// LEAD SUBMISSION
// ============================================

export interface FunnelLeadData {
  name: string;
  email: string;
  dolor: string;
  capacidad: string[];
  timing: string;
  inversion: string;
  semaforo_dolor: 'verde' | 'amarillo' | 'rojo';
  semaforo_capacidad: 'verde' | 'amarillo' | 'rojo';
  semaforo_timing: 'verde' | 'amarillo' | 'rojo';
  semaforo_inversion: 'verde' | 'amarillo' | 'rojo';
  semaforo_score: number; // 0-4 greens
}

export interface LeadSubmitResponse {
  success: boolean;
  lead_id?: number;
  message?: string;
  error?: string;
}

/** Submit a qualified lead to the backend */
export async function submitLead(lead: FunnelLeadData): Promise<LeadSubmitResponse> {
  try {
    const sessionId = generateSessionId();
    const utms = getUtmParams();

    const response = await fetch(`${API_BASE}/lead.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...lead,
        session_id: sessionId,
        source: utms.utm_source || 'direct',
        ...utms,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Error al enviar aplicación');
    }

    // Track the form completion event with the lead_id
    trackEvent('form_completed', {
      lead_id: data.lead_id,
      semaforo_score: lead.semaforo_score,
    });

    return data;
  } catch (error) {
    console.error('[FunnelTracker] Failed to submit lead:', error);
    return { success: false, error: String(error) };
  }
}

// ============================================
// SEMAFORO CALCULATOR
// ============================================

export type SemaforoColor = 'verde' | 'amarillo' | 'rojo';

export function calculateSemaforo(formData: {
  dolor: string;
  capacidad: string[];
  timing: string;
  inversion: string;
}): {
  dolor: SemaforoColor;
  capacidad: SemaforoColor;
  timing: SemaforoColor;
  inversion: SemaforoColor;
  score: number;
} {
  // DOLOR
  let dolor: SemaforoColor = 'rojo';
  if (formData.dolor === 'trabajo_datos_crecer' || formData.dolor === 'trabajo_it_cambiar') {
    dolor = 'verde';
  } else if (formData.dolor === 'estudiando') {
    dolor = 'amarillo';
  }

  // CAPACIDAD
  let capacidad: SemaforoColor = 'rojo';
  const hasSQL = formData.capacidad.includes('SQL');
  const hasPython = formData.capacidad.includes('Python');
  const hasDatabricks = formData.capacidad.includes('Databricks');
  const hasNone = formData.capacidad.includes('Ninguna');

  if (hasSQL && (hasPython || hasDatabricks)) {
    capacidad = 'verde';
  } else if (hasSQL || hasPython || formData.capacidad.includes('Excel') || formData.capacidad.includes('Power BI')) {
    capacidad = 'amarillo';
  } else if (hasNone) {
    capacidad = 'rojo';
  }

  // TIMING
  let timing: SemaforoColor = 'rojo';
  if (formData.timing === 'esta_semana' || formData.timing === 'este_mes') {
    timing = 'verde';
  } else if (formData.timing === 'en_2_3_meses') {
    timing = 'amarillo';
  }

  // INVERSION
  let inversion: SemaforoColor = 'rojo';
  if (formData.inversion === 'si_tengo') {
    inversion = 'verde';
  } else if (formData.inversion === 'puedo_conseguirlo') {
    inversion = 'amarillo';
  }

  const colors = [dolor, capacidad, timing, inversion];
  const score = colors.filter((c) => c === 'verde').length;

  return { dolor, capacidad, timing, inversion, score };
}

// ============================================
// CUSTOM HOOK HELPER: useTrackSection
// ============================================

/** React hook-compatible section tracking via ref callback */
export function createSectionTracker(sectionName: string) {
  let observer: IntersectionObserver | null = null;

  return {
    ref: (el: HTMLElement | null) => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      if (el) {
        observer = trackSectionView(sectionName, el);
      }
    },
    cleanup: () => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    },
  };
}
